import { router } from '@codesuma/baseline'
import utils from '../utils'
import supabase from './supabase'

// Push notifications — native (iOS/Android) only; on the web everything below
// is a no-op. The permission prompt is NOT shown at boot: enable() is called at
// the moments notifications become relevant (sharing a list, accepting a
// share), so the OS prompt appears with context the user understands.
//
// Capacitor (native) access via the injected global — matches supabase.ts.
const cap = () => (window as any).Capacitor
const plugin = () => cap()?.isNativePlatform?.() ? cap()?.Plugins?.PushNotifications : null

// The user's explicit "notifications off" choice (the menu toggle). While set,
// the boot-time token refresh and the contextual enable() prompts are skipped.
const OFF_FLAG = 'papa-push-off'
// Last token this device registered — kept so turning the toggle off can delete
// it server-side (pushes are sent to stored tokens even when the app is closed).
const TOKEN_KEY = 'papa-push-token'

let listenersAttached = false

const attachListeners = () => {
    const push = plugin()
    if (!push || listenersAttached) return
    listenersAttached = true

    // Fired after register(): Android delivers an FCM token, iOS an APNs token.
    // The server tells them apart by the platform field.
    push.addListener('registration', async ({ value }: { value: string }) => {
        try {
            localStorage.setItem(TOKEN_KEY, value)
            await supabase.auth.ensureSession()
            await utils.http.post('/api/v1/push/register', { token: value, platform: cap().getPlatform() })
        } catch (err) {
            console.warn('Push token registration failed:', err)
        }
    })

    push.addListener('registrationError', (err: any) => {
        console.warn('Push registration error:', err)
    })

    // Notification tapped (app in background or cold-started from it) — go to
    // the list it's about. Listeners are attached at boot, before any routing,
    // so the cold-start event finds the router ready.
    push.addListener('pushNotificationActionPerformed', (action: any) => {
        const listId = action?.notification?.data?.listId
        if (listId) router.goto(`/list/${listId}`)
    })
}

// Boot: route notification taps, and silently refresh the device token when
// permission was already granted earlier. Never prompts.
const init = async (): Promise<void> => {
    const push = plugin()
    if (!push) return
    attachListeners()
    if (localStorage.getItem(OFF_FLAG)) return // user turned notifications off
    try {
        const { receive } = await push.checkPermissions()
        if (receive === 'granted') await push.register()
    } catch (err) {
        console.warn('Push init failed:', err)
    }
}

// Ask for permission (OS prompt on first call) and register this device.
// No-op while the user has notifications switched off in the menu — the
// contextual share-time calls must not override an explicit choice.
const enable = async (): Promise<void> => {
    const push = plugin()
    if (!push || localStorage.getItem(OFF_FLAG)) return
    attachListeners()
    try {
        const { receive } = await push.requestPermissions()
        if (receive === 'granted') await push.register()
    } catch (err) {
        console.warn('Push enable failed:', err)
    }
}

// Whether this build has notifications at all (native with the plugin) — the
// menu hides its toggle otherwise; web has no push path.
const isSupported = (): boolean => !!plugin()

// Effective state for the menu toggle: OS permission granted and not switched off.
const isEnabled = async (): Promise<boolean> => {
    const push = plugin()
    if (!push || localStorage.getItem(OFF_FLAG)) return false
    try {
        const { receive } = await push.checkPermissions()
        return receive === 'granted'
    } catch (err) {
        return false
    }
}

// The menu toggle. On: clear the off flag and run the normal permission +
// register flow; returns the resulting state so the toggle can reflect an
// OS-level denial. Off: remember the choice and delete this device's token
// server-side, so pushes stop even while the app is closed.
const setEnabled = async (on: boolean): Promise<boolean> => {
    if (!plugin()) return false
    if (on) {
        localStorage.removeItem(OFF_FLAG)
        await enable()
        return isEnabled()
    }
    localStorage.setItem(OFF_FLAG, '1')
    const token = localStorage.getItem(TOKEN_KEY)
    if (token) {
        try {
            await supabase.auth.ensureSession()
            await utils.http.post('/api/v1/push/unregister', { token })
        } catch (err) {
            console.warn('Push token unregister failed:', err)
        }
    }
    return false
}

export default { init, enable, isSupported, isEnabled, setEnabled }
